import type { IconName } from '@customerio/pluma-icons';
import type { Delivery } from '@/services/db/delivery/entity';
import {
  allDrafted,
  anyFailedOrUndeliverable,
  hasRetryableSelection,
  hasSendableSelection,
  onlyDraftedSelected,
} from './selection';

/**
 * Local shape for a Pluma `DataTable` promoted bulk action (the library doesn't
 * re-export its type). Structurally assignable to `bulkActions.promotedActions`;
 * `onClick` takes just the selection map (a subset of Pluma's signature).
 */
interface PromotedBulkAction {
  label: string;
  icon?: IconName;
  variant?: 'secondary';
  size?: 'sm';
  isDanger?: boolean;
  isDisabled?: boolean;
  onClick?: (selection: Record<string, boolean>) => void;
}

export interface BulkActionHandlers {
  hasReadOnlyPermission: boolean;
  onBulkRetry?: (ids: string[]) => void;
  onBulkSend?: (ids: string[]) => void;
  onBulkDelete?: (ids: string[]) => void;
}

/** The selected row ids from Pluma's `Record<id, boolean>` selection state. */
function selectedIds(selection: Record<string, boolean>): string[] {
  return Object.keys(selection).filter((id) => selection[id]);
}

/**
 * The promoted bulk-action buttons for the deliveries table, ported from the
 * Ember `bulkActions` getter: a page of failed/undeliverable rows shows "Retry
 * selected", otherwise "Send selected"; drafts additionally show "Delete
 * selected". Each is disabled until the selection satisfies its predicate. The
 * actual mutation + confirmation is owned by the caller (page).
 */
export function buildDeliveryBulkActions(
  deliveries: readonly Delivery[],
  selected: readonly Delivery[],
  { hasReadOnlyPermission, onBulkRetry, onBulkSend, onBulkDelete }: BulkActionHandlers,
): PromotedBulkAction[] {
  const actions: PromotedBulkAction[] = [];

  if (anyFailedOrUndeliverable(deliveries)) {
    actions.push({
      label: 'Retry selected',
      icon: 'rematch',
      variant: 'secondary',
      size: 'sm',
      isDisabled: hasReadOnlyPermission || !hasRetryableSelection(selected),
      onClick: (selection: Record<string, boolean>) => onBulkRetry?.(selectedIds(selection)),
    });
  } else {
    actions.push({
      label: 'Send selected',
      icon: 'send-auto',
      variant: 'secondary',
      size: 'sm',
      isDisabled: hasReadOnlyPermission || !hasSendableSelection(selected),
      onClick: (selection: Record<string, boolean>) => onBulkSend?.(selectedIds(selection)),
    });
  }

  if (allDrafted(deliveries) || onlyDraftedSelected(selected)) {
    actions.push({
      label: 'Delete selected',
      icon: 'close',
      size: 'sm',
      isDanger: true,
      isDisabled: hasReadOnlyPermission || !onlyDraftedSelected(selected),
      onClick: (selection: Record<string, boolean>) => onBulkDelete?.(selectedIds(selection)),
    });
  }

  return actions;
}
